import { IApiCartItem, ITicket } from '../../domain/Cart.domain';

interface ICartItemTicketTotals {
    price: number;
    strikeOutPrice: number;
    booked: number;
    savings: number;
    discount?: number;
    subtotal?: number;
    subtotalFee?: number;
    subtotalFeeSubunits?: number;
    subTotalSubunits?: number;
    subtotalTax?: number;
    subtotalTaxSubunits?: number;
    totalSubunits?: number;
    total?: number;
    totalNet?: number;
    totalNetSubunits?: number;
    totalSavings?: number;
    totalSavingsString?: string;
    priceString?: string;
    strikeOutPriceString?: string;
    bookedString?: string;
    savingsString?: string;
    discountString?: string;
}

interface ICartItemTotals extends ICartItemTicketTotals {
    ticketTotals: ICartItemTicketTotals[];
}

const zeroTicketTotals: ICartItemTicketTotals = {
    price: 0,
    strikeOutPrice: 0,
    booked: 0,
    savings: 0,
    discount: 0,
    totalSavings: 0,
};

export const formatSubunits = (num?: number): string => {
    if (!num) {
        return '0.00';
    } else {
        return (num / 100).toFixed(2);
    }
};

const calcCartItemTickets = (ticketTotals: ICartItemTicketTotals[]) => {
    const totals = ticketTotals.reduce(
        (acc, cur) => ({
            price: acc.price + cur.price,
            strikeOutPrice: acc.strikeOutPrice + cur.strikeOutPrice,
            booked: acc.booked + cur.booked,
            savings: acc.savings + cur.savings,
            discount: acc.discount || 0 + (cur.discount || 0),
            totalSavings: (acc.totalSavings || 0) + (cur.totalSavings || 0),
        }),
        zeroTicketTotals
    );

    return {
        ...totals,
        priceString: formatSubunits(totals.price),
        strikeOutPriceString: formatSubunits(totals.strikeOutPrice),
        bookedString: String(totals.booked),
        savingsString: formatSubunits(totals.savings),
        discountString: formatSubunits(totals.discount),
    };
};

export const getSavingsFromTickets = (tickets?: ITicket[], discount?: number): ICartItemTotals => {
    if (!tickets || !tickets.length || !Array.isArray(tickets)) {
        return { ...zeroTicketTotals, ticketTotals: [] };
    }

    const ticketsValues: ICartItemTicketTotals[] = tickets.map(
        ({ price_subunits, strike_out_subunits, booked: bookedString }) => {
            const price = price_subunits || 0;
            const strikeOutPrice = strike_out_subunits || 0;
            const booked = Number(bookedString || '0');
            const savings =
                strikeOutPrice && strikeOutPrice > price ? booked * (strikeOutPrice - price) : 0;

            return {
                price,
                strikeOutPrice,
                booked,
                savings,
                priceString: formatSubunits(price),
                strikeOutPriceString: formatSubunits(strikeOutPrice),
                bookedString: String(booked),
                savingsString: formatSubunits(savings),
                discount: discount || 0,
                discountString: formatSubunits(discount || 0),
                totalSavings: savings,
                totalSavingsString: formatSubunits(savings),
            };
        }
    );

    const totals = calcCartItemTickets(ticketsValues);

    return {
        ...totals,
        ticketTotals: ticketsValues,
    };
};

export const getSavingsFromCartItems = (items: IApiCartItem[]) => {
    if (!items || !items.length || !Array.isArray(items)) {
        return { ...zeroTicketTotals, items };
    }

    const retValues = items.map((item) => ({
        ...item,
        ...getSavingsFromTickets(item.tickets),
        discount: item.discount_rate_subunits || 0,
        discountString: formatSubunits(item.discount_rate_subunits || 0),
        subtotal: (item.subtotal || 0) * 100,
        subtotalFee: (item.subtotal_fee || 0) * 100,
        subtotalFeeSubunits: item.subtotal_fee_subunits,
    }));

    const totals = calcCartItemTickets(retValues.map((item) => item));

    return {
        ...totals,
        totalSavings: totals.savings + (totals?.discount || 0),
        totalSavingsString: formatSubunits(totals.savings + (totals?.discount || 0)),
        itemsTotals: retValues,
    };
};
